<template>
  <section class="dd-effects">
    <div class="dd-effects__grid">
      <div class="dd-effects__main">
        <EffectHeroCard>
          <template #heading>
            <h2 class="dd-effects__hero-title">{{ heroTitle }}</h2>
          </template>
          <template #body>
            <p class="dd-effects__hero-text">{{ heroCopy }}</p>
            <p v-if="showFreePaymentMode && !donationOnlyMode" class="dd-effects__hero-hint">{{ $t('wall.effects.hero.free_hint') }}</p>
            <p v-if="isLive && !donationOnlyMode" class="dd-effects__hero-live">{{ $t('wall.effects.hero.live_hint') }}</p>
          </template>
          <template #actions>
            <SigninWithGoogle v-if="!authenticated && !donationOnlyMode" />
            <button
              v-else-if="showSubscriptionCTA"
              class="btn-orange"
              type="button"
              @click="goToSubscriptions"
            >
              {{ $t('wall.effects.hero.subscription_cta') }}
            </button>
          </template>
        </EffectHeroCard>
        <EffectModeSelector>
          <template #title>
            {{ modeTitle }}
          </template>
          <template #subtitle>
            {{ modeSubtitle }}
          </template>
          <ul class="dd-effects__mode-list">
            <payment-mode-item
              id="subscription"
              :title="$t('forms.payment_mode_subscription')"
              :disabled="$store.getters.effectPostInProgress"
              v-if="subscriptionModeSelectorVisible && hasSubscriptionLimits"
              :subtitle="subscriptionSubtitle"
            >
              <template #badge v-if="subscriptionBadge">
                <span class="dd-effects__mode-pill">{{ subscriptionBadge }}</span>
              </template>
              <template v-if="!$store.getters.authenticatedAsSubscriber && subscriptionsEnabled">
                <template v-if="authenticated">
                  <p class="dd-effects__mode-hint">{{ $t('sponsor.no_subscription') }}</p>
                  <subscription-levels />
                </template>
                <div v-if="!authenticated" class="dd-effects__signin">
                  <SigninWithGoogle href="/login?provider=google" />
                </div>
                <button
                  v-else
                  class="btn-orange dd-effects__mode-cta"
                  type="button"
                  @click="goToNewSubscription"
                >
                  {{ $t('sponsor.select_level') }}
                </button>
              </template>
              <template v-else-if="(!$store.getters.subscriber && subscriptionsEnabled)">
                <template v-if="authenticated">
                  <p class="dd-effects__mode-hint">{{ $t('sponsor.no_subscription') }}</p>
                  <subscription-levels />
                </template>
                <button class="btn-orange dd-effects__mode-cta" type="button" @click="goToNewSubscription">
                  {{ $t('sponsor.select_level') }}
                </button>
              </template>
              <template v-else-if="!$store.getters.freeEffectsAvailable">
                <router-link class="dd-effects__mode-link" to="/wall/subscription">
                  <span v-html="noFreeEffects" />
                </router-link>
              </template>
              <template v-else>
                <p class="dd-effects__mode-hint">{{ $t('sponsor.limit_prefix') }}</p>
                <ul class="dd-effects__limit-list">
                  <li v-for="effectClass in $store.getters.effectClasses" :key="effectClass.id">
                    <span class="nowrap">
                      {{ effectClass.name }} —
                      <span class="orange">{{ $store.getters.limits[effectClass.id] || 0 }}</span>
                    </span>
                  </li>
                </ul>
                <p class="dd-effects__mode-hint">
                  {{ $t('sponsor.ai_sticker_creation') }} —
                  <span class="orange">{{ $store.getters.freeGenerationSessionsLeft }}</span>.
                </p>
                <div class="dd-effects__mode-actions">
                  <button class="btn-orange" type="button" @click="goToSubscriptions">
                    {{ $t('subscriptions.settings') }}
                  </button>
                </div>
              </template>
            </payment-mode-item>

            <payment-mode-item
              v-for="method in $store.getters.allowedPaymentMethods"
              :key="method"
              :id="method"
              :title="$t('forms.payment_mode_' + method)"
              :disabled="$store.getters.effectPostInProgress"
              :subtitle="paymentModeSubtitle(method)"
            >
              <amount-input :disabled="$store.getters.effectPostInProgress" />
            </payment-mode-item>

            <payment-mode-item
              v-for="method in $store.getters.thirdPartyPaymentMethods"
              :key="method.id"
              :id="method.id"
              :title="method.text"
              :disabled="$store.getters.effectPostInProgress"
              :subtitle="thirdPartySubtitle(method)"
            >
              <a class="btn-orange" :href="method.url">{{ $t('forms.initiate_payment') }}</a>
            </payment-mode-item>

            <payment-mode-item
              v-if="showFreePaymentMode"
              id="free"
              :title="$store.getters.freeEffectsPaymentOptionName ? $store.getters.freeEffectsPaymentOptionName : $t('forms.free_effects_default_payment_option_name')"
              :disabled="!canSendFreeEffect"
              :subtitle="freeSubtitle"
            >
              <template #badge v-if="freeBadge">
                <span class="dd-effects__mode-pill dd-effects__mode-pill--ghost">{{ freeBadge }}</span>
              </template>
              <EffectFreeNotice v-if="secondsUntilNextFreeEffect > 0" tone="warning">
                {{ $t('forms.next_free_effect_available', { seconds: secondsUntilNextFreeEffect }) }}
              </EffectFreeNotice>
              <EffectFreeNotice v-if="showFreeGenerationSessionNotice" tone="accent">
                <p>
                  {{ $t('forms.free_session_for_sticker_creation_found') }}
                  <router-link :to="generationURL" class="orange">
                    {{ $store.getters.haveUnspentAttempts ? $t('forms.you_can_create_a_sticker') : $t('forms.you_can_select_one_of_the_stickers') }}
                  </router-link>
                </p>
              </EffectFreeNotice>
            </payment-mode-item>
          </ul>
        </EffectModeSelector>

        <div v-if="paymentModeAvailable && !effectSelectorEnabled" class="dd-effects__card dd-wall-card">
          <EffectPostingForm />
        </div>

        <div v-if="paymentModeAvailable && effectSelectorEnabled" class="dd-effects__card dd-wall-card dd-effects__selector">
          <div class="dd-effects__section-title">{{ $t('forms.select_effect') }}</div>
          <EffectList />
          <div v-if="selectedEffect" class="dd-effects__card dd-wall-card dd-effects__selector-form dd-effects__selector-text-form">
            <div class="dd-effects__section-title">{{ selectedEffect.name }}</div>
            <EffectPostingForm />
          </div>
        </div>
      </div>

      <EffectSummarySidebar v-if="sidebarVisible">
        <div v-if="currentSubscriptionSummary" class="dd-effects__sidebar-card dd-wall-card">
          <h3 class="dd-effects__sidebar-title">{{ $t('wall.effects.sidebar.subscription_title') }}</h3>
          <p class="dd-effects__sidebar-text">{{ $t('wall.effects.sidebar.subscription_level', { level: currentSubscriptionSummary.levelName }) }}</p>
          <p class="dd-effects__sidebar-meta">{{ currentSubscriptionSummary.price }}</p>
          <p v-if="currentSubscriptionSummary.paidTill" class="dd-effects__sidebar-text">{{ $t('wall.effects.sidebar.subscription_paid_till', { date: currentSubscriptionSummary.paidTill }) }}</p>
          <button class="btn-orange dd-effects__sidebar-btn" type="button" @click="goToSubscriptions">{{ $t('wall.effects.sidebar.manage_subscription') }}</button>
        </div>
        <div v-if="freeLimits.length" class="dd-effects__sidebar-card dd-wall-card">
          <h3 class="dd-effects__sidebar-title">{{ $t('wall.effects.sidebar.free_limits') }}</h3>
          <ul class="dd-effects__sidebar-list">
            <li v-for="limit in freeLimits" :key="limit.id">
              {{ limit.text }}
            </li>
            <li>
              {{ $t('sponsor.ai_sticker_creation') }}: {{ $store.getters.freeGenerationSessionsLeft }}
            </li>
          </ul>
        </div>

        <div v-if="freeGenerationSummary" class="dd-effects__sidebar-card dd-wall-card">
          <h3 class="dd-effects__sidebar-title">{{ $t('wall.effects.sidebar.ai_title') }}</h3>
          <p class="dd-effects__sidebar-text">{{ freeGenerationSummary }}</p>
        </div>

        <div v-if="nextFreeEffectCopy" class="dd-effects__sidebar-card dd-wall-card">
          <h3 class="dd-effects__sidebar-title">{{ $t('wall.effects.sidebar.next_free') }}</h3>
          <p class="dd-effects__sidebar-text">{{ nextFreeEffectCopy }}</p>
        </div>
      </EffectSummarySidebar>
    </div>
  </section>
</template>

<script>
import EffectList from "@/new/components/EffectList";
import EffectPostingForm from "@/new/components/EffectPostingForm";
import AmountInput from "@/new/components/general/AmountInput";
import PaymentModeItem from "@/new/components/PaymentModeItem";
import SubscriptionLevels from "@/new/components/SubscriptionLevels";
import SigninWithGoogle from "@/new/components/general/SigninWithGoogle.vue";
import EffectHeroCard from "@/new/components/effects/EffectHeroCard.vue";
import EffectModeSelector from "@/new/components/effects/EffectModeSelector.vue";
import EffectSummarySidebar from "@/new/components/effects/EffectSummarySidebar.vue";
import EffectFreeNotice from "@/new/components/effects/EffectFreeNotice.vue";
import moment from 'moment';
import SubscriptionStatus from "@/store/models/SubscriptionStatus";
import { EffectGenerationSessionStatus } from "@/store/models/EffectGenerationSession";

export default {
  name: 'WallEffects',
  components: {
    EffectList,
    EffectPostingForm,
    AmountInput,
    PaymentModeItem,
    SubscriptionLevels,
    SigninWithGoogle,
    EffectHeroCard,
    EffectModeSelector,
    EffectSummarySidebar,
    EffectFreeNotice,
  },
  computed: {
    showFreeGenerationSessionNotice() {
      return this.$store.getters.effectGenerationSession?.status === EffectGenerationSessionStatus.Free && this.$store.getters.secondsUntilNextFreeEffect > 0 && this.$store.getters.freeGenerationEnabled;
    },
    generationURL() { return `/generate?session=${this.$store.getters.effectGenerationSession?.uuid}`; },
    secondsUntilNextFreeEffect() { return this.$store.getters.secondsUntilNextFreeEffect; },
    canSendFreeEffect() {
      return !this.$store.getters.effectPostInProgress && this.$store.getters.secondsUntilNextFreeEffect <= 0 && (this.$store.getters.freeEffectsEnabled || this.$store.getters.freeGenerationEnabled);
    },
    effects() {
      const result = {};
      for (const effect of this.$store.getters.effects) { result[effect.id] = effect; }
      return result;
    },
    selectedEffect() { return this.effects[this.$store.getters.selectedEffect]; },
    noFreeEffects() {
      const url = '/wall/subscription';
      if (this.$store.getters.currentSubscription?.status === SubscriptionStatus.Paid) {
        return url ? this.$t('sponsor.no_effects', { href: url }) : this.$t('sponsor.no_effects_without_url');
      }
      return this.$t('sponsor.no_active_subscription', { href: url });
    },
    subscriptionModeSelectorVisible() { return this.$store.getters.subscriptionModeSelectorVisible; },
    hasSubscriptionLimits() { return this.$store.getters.hasSubscriptionLimits; },
    channelName() { return this.$store.getters.visuals?.name || ''; },
    donationOnlyMode() { return this.$store.getters.donationOnlyMode; },
    heroTitle() {
      return this.donationOnlyMode ? this.$t('wall.effects.hero.title_donation_only', { name: this.channelName }) : this.$t('wall.effects.hero.title', { name: this.channelName });
    },
    heroCopy() {
      if (this.donationOnlyMode) {
        return this.$t('wall.effects.hero.text_donation_only', { name: this.channelName });
      }
      if (!this.authenticated) {
        return this.$t('wall.effects.hero.text_guest', { name: this.channelName });
      }
      if (this.isSponsor) {
        return this.$t('wall.effects.hero.text_sponsor');
      }
      return this.$t('wall.effects.hero.text_supporter');
    },
    modeTitle() {
      return this.donationOnlyMode ? this.$t('wall.effects.mode.title_donation_only') : this.$t('wall.effects.mode.title');
    },
    modeSubtitle() {
      return this.donationOnlyMode ? this.$t('wall.effects.mode.subtitle_donation_only') : this.$t('wall.effects.mode.subtitle');
    },
    isLive() { return this.$store.getters.isLive; },
    authenticated() { return this.$store.getters.authenticated; },
    isSponsor() { return this.$store.getters.isSponsor; },
    subscriptionsEnabled() { return this.$store.getters.subscriptionsEnabled; },
    hasActiveSubscription() { return !!this.$store.getters.currentSubscription; },
    subscriptionSubtitle() { return this.i18nOrNull('wall.effects.mode.subscription_subtitle'); },
    subscriptionBadge() {
      if (this.hasActiveSubscription) {
        return this.i18nOrNull('wall.effects.mode.badge_active');
      }
      if (this.subscriptionsEnabled) {
        return this.i18nOrNull('wall.effects.mode.badge_members');
      }
      return '';
    },
    freeSubtitle() { return this.i18nOrNull('wall.effects.mode.free_subtitle'); },
    freeBadge() {
      if (this.showFreePaymentMode) {
        return this.i18nOrNull('wall.effects.mode.badge_free');
      }
      return '';
    },
    showSubscriptionCTA() { return !this.donationOnlyMode && this.authenticated && !this.isSponsor && this.subscriptionsEnabled; },
    paymentModeAvailable() { return this.$store.getters.paymentModeAvailable; },
    effectSelectorEnabled() { return this.$store.getters.effectSelectorEnabled; },
    showFreePaymentMode() { return this.$store.getters.freeEffectsEnabled || this.$store.getters.freeGenerationEnabled; },
    currentSubscriptionSummary() {
      const subscription = this.$store.getters.currentSubscription;
      const level = this.$store.getters.currentLevel;
      if (!subscription || !level) {
        return null;
      }
      const periodKey = level.paymentPeriod ? `periods.${level.paymentPeriod}` : null;
      const periodLabel = periodKey && this.$i18n.te(periodKey) ? this.$t(periodKey) : level.paymentPeriod || '';
      const price = level.price && level.currency ? `${level.price} ${level.currency}/${periodLabel}` : null;
      const paidTill = subscription.paidTill ? moment(subscription.paidTill).format(this.$t('date_time_format')) : null;
      return {
        levelName: level.name,
        price,
        paidTill,
      };
    },
    freeLimits() {
      const classes = this.$store.getters.effectClasses || [];
      const limits = this.$store.getters.limits || {};
      return classes
        .map((cls) => {
          const remaining = limits[cls.id] || 0;
          if (remaining <= 0) { return null; }
          return {
            id: cls.id,
            text: this.$t('wall.effects.sidebar.limit_entry', { name: cls.name, count: remaining }),
          };
        })
        .filter(Boolean);
    },
    freeGenerationSummary() {
      if (!this.$store.getters.freeGenerationEnabled) {
        return null;
      }
      const sessionsLeft = this.$store.getters.freeGenerationSessionsLeft;
      if (sessionsLeft > 0) {
        return this.$t('wall.effects.sidebar.ai_left', { count: sessionsLeft });
      }
      return this.$t('wall.effects.sidebar.ai_empty');
    },
    nextFreeEffectCopy() {
      if (!this.$store.getters.freeEffectsEnabled) {
        return null;
      }
      const seconds = this.secondsUntilNextFreeEffect;
      if (seconds <= 0) {
        return this.$t('wall.effects.sidebar.free_ready');
      }
      return this.$t('wall.effects.sidebar.free_in', { seconds });
    },
    sidebarVisible() {
      // Hide entire sidebar if subscriptions are disabled
      if (!this.subscriptionsEnabled) return false;
      return !!(this.currentSubscriptionSummary || this.freeLimits.length || this.freeGenerationSummary || this.nextFreeEffectCopy);
    },
  },
  methods: {
    i18nOrNull(key, params = {}) {
      return this.$i18n && this.$i18n.te(key) ? this.$t(key, params) : '';
    },
    paymentModeSubtitle(method) {
      if (!method) {
        return '';
      }
      const key = `wall.effects.mode.${method}_subtitle`;
      return this.i18nOrNull(key);
    },
    thirdPartySubtitle(method) {
      if (!method) {
        return '';
      }
      const key = `wall.effects.mode.${method.id}_subtitle`;
      const translated = this.i18nOrNull(key);
      if (translated) {
        return translated;
      }
      return method.subtitle || method.description || '';
    },
    goToNewSubscription() { this.$router.push('/wall/subscription'); },
    goToSubscriptions() { this.$router.push('/wall/subscription'); },
  }
}
</script>
